import React, { useState } from 'react';
import { Layout, Menu, Button, Dropdown, Avatar, Typography, Space, Badge, Popover, List, Empty, Spin } from 'antd';
import {
  DashboardOutlined,
  TeamOutlined,
  SettingOutlined,
  UserOutlined,
  LogoutOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  BellOutlined,
  TruckOutlined
} from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useQuery } from '@tanstack/react-query';
import { type Notification } from '../services/notificationService';
import { apiClient } from '../services/api';

const { Header, Sider, Content } = Layout;
const { Text } = Typography;

interface SuperAdminLayoutProps {
  children: React.ReactNode;
}

const SuperAdminLayout: React.FC<SuperAdminLayoutProps> = ({ children }) => {
  const [collapsed, setCollapsed] = useState(false);
  const [notificationsVisible, setNotificationsVisible] = useState(false);
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Récupérer les notifications récentes avec React Query
  const { data: recentNotifications, isLoading: notificationsLoading } = useQuery({
    queryKey: ['super-admin-recent-notifications'],
    queryFn: () => apiClient.get<{ data: Notification[] }>('/super-admin/notifications/recent?limit=3').then(r => r.data),
    refetchInterval: 30000, // Actualiser toutes les 30 secondes
    staleTime: 30000,
    retry: false, // Don't retry if API is not available
  });

  // Récupérer le nombre de notifications non lues
  const { data: unreadData } = useQuery({
    queryKey: ['super-admin-unread-count'],
    queryFn: () => apiClient.get<{ unread_count: number }>('/super-admin/notifications/unread-count'),
    refetchInterval: 30000,
    staleTime: 30000,
    retry: false,
  });

  const unreadCount = unreadData?.unread_count || 0;

  // Gestion des notifications
  const handleNotificationClick = () => {
    setNotificationsVisible(!notificationsVisible);
  };

  const handleNotificationItemClick = (_notificationId: string) => {
    // Marquer comme lue et naviguer vers les détails si nécessaire
    navigate('/super-admin/notifications');
    setNotificationsVisible(false);
  };

  const handleViewAllNotifications = () => {
    navigate('/super-admin/notifications');
    setNotificationsVisible(false);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));
    
    if (diffInMinutes < 60) {
      return `il y a ${diffInMinutes} min`;
    } else if (diffInMinutes < 1440) { // 24 hours
      return `il y a ${Math.floor(diffInMinutes / 60)}h`;
    } else {
      return date.toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    }
  };

  // Contenu du popover des notifications
  const notificationContent = (
    <div style={{ width: 300 }}>
      <div style={{ 
        padding: '8px 16px', 
        borderBottom: '1px solid #f0f0f0',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <Text strong>Notifications</Text>
        <Button 
          type="link" 
          size="small"
          onClick={handleViewAllNotifications}
        >
          Voir tout
        </Button>
      </div>
      
      {notificationsLoading ? (
        <div style={{ padding: '20px', textAlign: 'center' }}>
          <Spin />
        </div>
      ) : recentNotifications && recentNotifications.length > 0 ? (
        <List
          size="small"
          dataSource={recentNotifications}
          renderItem={(item: Notification) => (
            <List.Item
              style={{ 
                cursor: 'pointer',
                backgroundColor: item.read ? 'transparent' : '#f6ffed',
                padding: '12px 16px'
              }}
              onClick={() => handleNotificationItemClick(item.id)}
            >
              <List.Item.Meta
                title={
                  <Text style={{ fontSize: '14px', fontWeight: item.read ? 'normal' : 'bold' }}>
                    {item.title}
                  </Text>
                }
                description={
                  <div>
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                      {item.description}
                    </Text>
                    <br />
                    <Text type="secondary" style={{ fontSize: '11px' }}>
                      {formatDate(item.created_at)}
                    </Text>
                  </div>
                }
              />
            </List.Item>
          )}
        />
      ) : (
        <div style={{ padding: '20px' }}>
          <Empty 
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="Aucune notification"
          />
        </div>
      )}
    </div>
  );

  // Éléments du menu principal
  const menuItems = [
    {
      key: '/super-admin',
      icon: <DashboardOutlined />,
      label: 'Tableau de bord',
      onClick: () => navigate('/super-admin'),
    },
    {
      key: '/super-admin/tenants',
      icon: <TeamOutlined />,
      label: 'Entreprises',
      onClick: () => navigate('/super-admin/tenants'),
    },
    {
      key: 'settings',
      icon: <SettingOutlined />,
      label: 'Configuration',
      children: [
        {
          key: '/super-admin/settings/system',
          label: 'Système',
          onClick: () => navigate('/super-admin/settings'),
        },
        {
          key: '/super-admin/settings/users',
          label: 'Utilisateurs',
          onClick: () => navigate('/super-admin/settings/users'), // ✅ Implémenté
        },
      ],
    },
  ];
  
  // Menu du profil utilisateur
  const profileMenuItems = [
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: 'Mon profil',
      onClick: () => navigate('/super-admin/profile'), // ✅ Implémenté
    },
    {
      key: 'settings',
      icon: <SettingOutlined />,
      label: 'Paramètres',
      onClick: () => navigate('/super-admin/settings'),
    },
    {
      type: 'divider' as const,
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: 'Déconnexion',
      onClick: signOut,
      danger: true,
    },
  ];

  // Déterminer l'élément de menu sélectionné
  const getSelectedKey = () => {
    const path = location.pathname;
    
    // Handle dashboard route
    if (path === '/super-admin' || path === '/super-admin/dashboard') {
      return ['/super-admin'];
    }
    
    // Handle tenants route
    if (path.startsWith('/super-admin/tenants')) {
      return ['/super-admin/tenants'];
    }
    
    // Handle settings routes
    if (path.startsWith('/super-admin/settings')) {
      return [path];
    }
    
    return ['/super-admin'];
  };

  return (
    <Layout style={{ minHeight: '100vh' }}>
      {/* Sidebar */}
      <Sider 
        trigger={null} 
        collapsible 
        collapsed={collapsed}
        style={{
          background: '#001529',
        }}
      >
        {/* Logo */}
        <div style={{
          height: '64px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'rgba(255, 255, 255, 0.1)',
          margin: '16px',
          borderRadius: '6px',
          color: '#fff',
          cursor: 'pointer',
        }}
        onClick={() => navigate('/super-admin')}
        >
          <TruckOutlined style={{ fontSize: '24px', marginRight: collapsed ? 0 : '8px' }} />
          {!collapsed && (
            <span style={{ fontSize: '18px', fontWeight: 'bold' }}>
              FleetBase
            </span>
          )}
        </div>

        {/* Menu principal */}
        <Menu
          theme="dark"
          mode="inline"
          selectedKeys={getSelectedKey()}
          style={{ borderRight: 0 }}
          items={menuItems}
        />

        {/* Badge Super Admin */}
        {!collapsed && (
          <div style={{
            position: 'absolute',
            bottom: '16px',
            left: '16px',
            right: '16px',
            background: 'rgba(255, 255, 255, 0.1)',
            padding: '8px',
            borderRadius: '4px',
            textAlign: 'center',
          }}>
            <Badge 
              status="processing" 
              text={
                <Text style={{ color: '#fff', fontSize: '12px' }}>
                  Super Admin
                </Text>
              } 
            />
          </div>
        )}
      </Sider>

      {/* Layout principal */}
      <Layout>
        {/* Header */}
        <Header style={{
          padding: '0 24px',
          background: '#fff',
          borderBottom: '1px solid #f0f0f0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
        }}>
          {/* Bouton collapse et titre */}
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <Button
              type="text"
              icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
              onClick={() => setCollapsed(!collapsed)}
              style={{
                fontSize: '16px',
                width: 64,
                height: 64,
              }}
            />
            <div style={{ lineHeight: 0 }}>
              <Text strong style={{ fontSize: '16px' }}>
                Administration Globale
              </Text>
              <div>
                <Text type="secondary" style={{ fontSize: '12px' }}>
                  Gestion de la plateforme FleetBase
                </Text>
              </div>
            </div>
          </div>

          {/* Actions utilisateur */}
          <Space size="middle">
            {/* Notifications avec popover */}
            <Popover
              content={notificationContent}
              title={null}
              trigger="click"
              open={notificationsVisible}
              onOpenChange={setNotificationsVisible}
              placement="bottomRight"
            >
              <Badge count={unreadCount} size="small">
                <Button
                  type="text"
                  icon={<BellOutlined />}
                  style={{ fontSize: '16px' }}
                  onClick={handleNotificationClick} // ✅ Implémenté
                />
              </Badge>
            </Popover>

            {/* Profil utilisateur */}
            <Dropdown
              menu={{ items: profileMenuItems }}
              placement="bottomRight"
              trigger={['click']}
            >
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                cursor: 'pointer',
                padding: '4px 8px',
                borderRadius: '4px',
                transition: 'background-color 0.3s',
              }}>
                <Avatar 
                  size="small" 
                  icon={<UserOutlined />}
                  style={{ marginRight: '8px', height: '38px', width:'38px', fontSize: '24px' }}
                />
                <div>
                  <div style={{ fontSize: '14px', fontWeight: 'bold', lineHeight: '16px' }}>
                    {user?.first_name} {user?.last_name}
                  </div>
                  <div style={{ fontSize: '12px', color: '#666', lineHeight: '24px' }}>
                    Super Administrateur
                  </div>
                </div>
              </div>
            </Dropdown>
          </Space>
        </Header>

        {/* Contenu principal */}
        <Content style={{
          margin: 0,
          background: '#f5f5f5',
          minHeight: 'calc(100vh - 64px)',
          overflow: 'auto',
        }}>
          {children}
        </Content>
      </Layout>
    </Layout>
  );
};

export default SuperAdminLayout;